import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Lightning, ArrowsClockwise, CaretDown, CaretRight, Sparkle, ChatCircleText, PaperPlaneRight, ChartBar } from "@phosphor-icons/react";
import api, { errDetail } from "@/lib/api";
import { PageShell } from "@/components/PageShell";
import { PriorityCard } from "@/components/PriorityCard";
import { CalibrationModal } from "@/components/CalibrationModal";
import { t } from "@/lib/i18n";

export default function PriorityPage() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [denied, setDenied] = useState(false);
  const [showRest, setShowRest] = useState(false);
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [teachOpen, setTeachOpen] = useState(false);
  const [teachMsgs, setTeachMsgs] = useState([]);
  const [teachInput, setTeachInput] = useState("");
  const [teachBusy, setTeachBusy] = useState(false);
  const [proposal, setProposal] = useState(null);
  const [banner, setBanner] = useState(null);
  const [calOpen, setCalOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data: d } = await api.get("/priority", { params: { rest_limit: 50 } });
      setData(d); setDenied(false);
    } catch (e) {
      if (e?.response?.status === 403) setDenied(true);
      else toast.error(errDetail(e));
    } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    load();
    const iv = setInterval(load, 30000);
    api.get("/priority/report").then((r) => setBanner(r.data.banner)).catch(() => {});
    try {
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      if (tz && localStorage.getItem("agi_tz_sent") !== tz) {
        api.post("/me/tz", { tz }).then(() => localStorage.setItem("agi_tz_sent", tz)).catch(() => {});
      }
    } catch { /* ignore */ }
    return () => clearInterval(iv);
  }, [load]);

  const analyze = async () => {
    setAnalyzing(true);
    try {
      const { data: r } = await api.post("/priority/backfill");
      toast.success(t("pi.analyze_queued", "Analyzing {n} conversations — check back in a moment", { n: r.queued }));
      setTimeout(() => { load(); setAnalyzing(false); }, 6000);
    } catch (e) { toast.error(errDetail(e)); setAnalyzing(false); }
  };

  const openThread = (tid) => navigate(`/app?thread=${tid}`);

  const feedback = async (sig, verdict, until) => {
    try {
      await api.post("/priority/feedback", { thread_id: sig.thread_id, verdict, until });
      if (verdict === "dismiss") {
        toast(t("pi.dismissed_toast", "Card dismissed — it returns when something new arrives"), {
          duration: 5000,
          action: { label: t("pi.undo", "Undo"), onClick: () => feedback(sig, "undo_dismiss") },
        });
      } else if (verdict === "snooze") {
        toast.success(t("pi.snoozed_toast", "Snoozed — it comes back at the time you chose (or sooner if something new arrives)"));
      } else if (verdict === "should_be_top") {
        toast.success(t("pi.top_toast", "Thanks — noted that this should have ranked higher"));
      } else if (verdict === "up" || verdict === "down") {
        toast.success(verdict === "up" ? t("pi.up_toast", "Thanks — scored right") : t("pi.down_toast", "Thanks — noted as scored wrong"));
      } else if (verdict === "undo_dismiss") {
        toast.success(t("pi.restored_toast", "Card restored"));
      }
      load();
    } catch (e) { toast.error(errDetail(e)); }
  };

  const teachSend = async () => {
    const msg = teachInput.trim();
    if (!msg || teachBusy) return;
    setTeachBusy(true); setTeachInput(""); setProposal(null);
    const history = [...teachMsgs, { role: "user", text: msg }];
    setTeachMsgs(history);
    try {
      const { data: r } = await api.post("/priority/teach", { message: msg, history: teachMsgs });
      setTeachMsgs([...history, { role: "agent", text: r.reply }]);
      if (r.action === "propose") setProposal(r.proposal);
    } catch (e) { toast.error(errDetail(e)); }
    finally { setTeachBusy(false); }
  };

  const teachConfirm = async () => {
    try {
      await api.post("/priority/teach/confirm", { proposal });
      toast.success(t("pi.teach_saved", "Saved: {s} — undo anytime in Memory", { s: proposal.summary || "" }));
      setProposal(null); setTeachMsgs([]); setTeachOpen(false);
      setTimeout(load, 2000);
    } catch (e) { toast.error(errDetail(e)); }
  };

  if (denied) {
    return (
      <PageShell title={t("pi.page_title", "Priority")} testId="priority-page">
        <div data-testid="priority-upgrade-card" className="border border-gray-200 rounded-md p-8 text-center max-w-lg">
          <Lightning size={32} className="text-amber-500 mx-auto mb-3" weight="fill" />
          <h2 className="font-heading font-bold text-gray-900 mb-1">{t("pi.upgrade_title", "Priority Inbox is not included in your plan")}</h2>
          <p className="text-sm text-gray-500 mb-4">{t("pi.upgrade_desc", "Upgrade to a plan with the ⚡ Priority inbox to get your mail ranked by what needs you.")}</p>
          <button data-testid="priority-upgrade-btn" onClick={() => navigate("/billing")}
            className="bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-md px-5 py-2.5 transition-colors">{t("pi.upgrade_btn", "View plans")}</button>
        </div>
      </PageShell>
    );
  }

  const buckets = data?.buckets || { now: [], later: [], rest: [], dismissed: [] };
  const empty = !loading && data && data.total === 0;

  const Section = ({ id, label, items, count }) => (
    <div className="mb-6" data-testid={`priority-section-${id}`}>
      <div className="flex items-center gap-2 mb-2">
        <span className={`text-[11px] font-bold tracking-wider ${id === "now" ? "text-red-600" : id === "later" ? "text-amber-600" : "text-gray-400"}`}>
          {label} ({count})
        </span>
        <div className="flex-1 h-px bg-gray-200" />
      </div>
      {items.map((s) => <PriorityCard key={s.thread_id} sig={s} onOpen={openThread} onFeedback={feedback} />)}
      {items.length === 0 && <p className="text-xs text-gray-400 px-1">—</p>}
    </div>
  );

  return (
    <PageShell title={t("pi.page_title", "Priority")}
      subtitle={t("pi.page_sub", "Your inbox ordered by what needs you — nothing is moved or deleted.")} testId="priority-page">
      <div className="flex items-center gap-2 mb-4 -mt-2">
        <button data-testid="priority-refresh-btn" onClick={load}
          className="inline-flex items-center gap-1.5 text-xs font-semibold border border-gray-300 hover:bg-gray-50 rounded-md px-3 py-1.5 transition-colors">
          <ArrowsClockwise size={13} /> {t("common.refresh", "Refresh")}
        </button>
        <button data-testid="priority-analyze-btn" onClick={analyze} disabled={analyzing}
          className="inline-flex items-center gap-1.5 text-xs font-semibold border border-blue-200 text-blue-700 hover:bg-blue-50 rounded-md px-3 py-1.5 transition-colors disabled:opacity-50">
          <Sparkle size={13} /> {t("pi.analyze_btn", "Analyze my inbox")}
        </button>
        <button data-testid="priority-report-link" onClick={() => navigate("/priority/report")}
          className="inline-flex items-center gap-1.5 text-xs font-semibold border border-gray-300 hover:bg-gray-50 rounded-md px-3 py-1.5 transition-colors">
          <ChartBar size={13} /> {t("pi.report_btn", "Report")}
        </button>
      </div>

      {banner && banner.learning && (
        <div data-testid="priority-learning-banner" className="border border-blue-200 bg-blue-50 text-blue-800 text-sm rounded-md px-4 py-2.5 mb-4">
          {banner.low_volume
            ? t("pi.banner_paused", "Learning paused — low volume this week")
            : t("pi.banner_learning", "Learning your priorities — accuracy improves with your feedback (day {n})", { n: banner.days_in })}
        </div>
      )}
      {banner && !banner.learning && banner.latest_pct !== null && banner.latest_pct !== undefined && (
        <div data-testid="priority-accuracy-banner" className="border border-gray-200 bg-gray-50 text-gray-600 text-xs rounded-md px-4 py-2 mb-4">
          {banner.low_volume
            ? t("pi.banner_paused", "Learning paused — low volume this week")
            : t("pi.banner_accuracy", "week {w} · accuracy {p}%", { w: banner.weeks_active, p: banner.latest_pct })}
        </div>
      )}
      {banner && !banner.calibrated && (
        <div data-testid="priority-calibrate-prompt" className="border border-amber-200 bg-amber-50/60 rounded-md px-4 py-3 mb-4 flex items-center gap-3">
          <span className="text-sm text-amber-800 flex-1">{t("pi.cal_prompt", "New here? Sort 10 recent emails to set your starting priorities in 60 seconds.")}</span>
          <button data-testid="priority-calibrate-btn" onClick={() => setCalOpen(true)}
            className="bg-amber-500 hover:bg-amber-600 text-white text-xs font-semibold rounded-md px-3 py-1.5 shrink-0">{t("pi.cal_start", "Start calibration")}</button>
        </div>
      )}
      {calOpen && <CalibrationModal onClose={() => setCalOpen(false)}
        onDone={() => { setCalOpen(false); setBanner({ ...banner, calibrated: true }); setTimeout(load, 1500); }} />}

      <div data-testid="priority-teach-box" className="border border-gray-200 rounded-md mb-5">
        <button data-testid="priority-teach-toggle" onClick={() => setTeachOpen(!teachOpen)}
          className="w-full flex items-center gap-2 px-3 py-2.5 text-sm text-gray-600 hover:bg-gray-50 transition-colors">
          <ChatCircleText size={16} className="text-blue-600" />
          <span className="flex-1 text-left">{t("pi.teach_ph", "Teach me: what did I get wrong?")}</span>
          {teachOpen ? <CaretDown size={13} /> : <CaretRight size={13} />}
        </button>
        {teachOpen && (
          <div className="border-t border-gray-100 p-3">
            {teachMsgs.map((m, i) => (
              <div key={i} data-testid={`teach-msg-${m.role}-${i}`}
                className={`text-sm mb-2 ${m.role === "user" ? "text-gray-900" : "text-blue-800 bg-blue-50 rounded-md px-3 py-2"}`}>
                <span className="font-semibold">{m.role === "user" ? t("pi.you", "You") : t("pi.agent", "Agent")}: </span>{m.text}
              </div>
            ))}
            {proposal && (
              <div data-testid="teach-proposal" className="border border-blue-200 bg-blue-50 rounded-md p-3 mb-2">
                <p className="text-sm text-gray-800 mb-2">{proposal.summary}</p>
                <div className="flex gap-2">
                  <button data-testid="teach-confirm-btn" onClick={teachConfirm}
                    className="bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold rounded-md px-3 py-1.5">{t("pi.teach_confirm", "Confirm & save to Memory")}</button>
                  <button data-testid="teach-cancel-btn" onClick={() => setProposal(null)}
                    className="text-xs text-gray-500 hover:text-gray-800 px-2">{t("pi.teach_cancel", "Cancel")}</button>
                </div>
              </div>
            )}
            <div className="flex gap-2">
              <input data-testid="priority-teach-input" value={teachInput} onChange={(e) => setTeachInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") teachSend(); }}
                placeholder={t("pi.teach_input_ph", "e.g. Anything from Oscar is always urgent")}
                className="flex-1 border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              <button data-testid="priority-teach-send" onClick={teachSend} disabled={teachBusy}
                className="inline-flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-md px-4 disabled:opacity-50">
                <PaperPlaneRight size={14} /> {teachBusy ? "…" : t("pi.teach_send", "Send")}
              </button>
            </div>
          </div>
        )}
      </div>

      {data?.paused && (
        <div data-testid="priority-paused-banner" className="border border-amber-300 bg-amber-50 text-amber-800 text-sm rounded-md px-4 py-2.5 mb-5">
          {t("pi.paused_banner", "Paused — token fund empty. Classification resumes when tokens are available.")}
        </div>
      )}

      {loading && <p className="text-sm text-gray-400">{t("common.loading", "Loading…")}</p>}

      {empty && (
        <div data-testid="priority-empty-state" className="border border-dashed border-gray-300 rounded-md p-10 text-center max-w-lg">
          <Lightning size={28} className="text-gray-300 mx-auto mb-2" />
          <h3 className="font-heading font-bold text-gray-900 mb-1">{t("pi.empty_title", "Nothing analyzed yet")}</h3>
          <p className="text-sm text-gray-500 mb-4">{t("pi.empty_desc", "Analyze your latest inbox emails to build your priority view.")}</p>
          <button data-testid="priority-empty-analyze-btn" onClick={analyze} disabled={analyzing}
            className="bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-md px-5 py-2.5 transition-colors disabled:opacity-50">
            {t("pi.analyze_btn", "Analyze my inbox")}
          </button>
        </div>
      )}

      {!loading && data && !empty && (
        <>
          <Section id="now" label={t("pi.act_now", "ACT NOW")} items={buckets.now} count={buckets.now.length} />
          <Section id="later" label={t("pi.act_later", "ACT LATER")} items={buckets.later} count={buckets.later.length} />
          <div className="mb-6" data-testid="priority-section-rest">
            <button data-testid="priority-rest-toggle" onClick={() => setShowRest(!showRest)}
              className="w-full flex items-center gap-2 mb-2 group">
              <span className="text-[11px] font-bold tracking-wider text-gray-400">
                {t("pi.everything_else", "EVERYTHING ELSE")} ({data.rest_total ?? buckets.rest.length})
              </span>
              <div className="flex-1 h-px bg-gray-200" />
              <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-blue-600 group-hover:text-blue-800">
                {showRest ? <CaretDown size={12} /> : <CaretRight size={12} />} {showRest ? t("pi.hide", "hide") : t("pi.show", "show")}
              </span>
            </button>
            {showRest && buckets.rest.map((s) => <PriorityCard key={s.thread_id} sig={s} onOpen={openThread} onFeedback={feedback} inRest />)}
            {showRest && buckets.rest.length === 0 && <p className="text-xs text-gray-400 px-1">—</p>}
            {showRest && (buckets.dismissed || []).length > 0 && (
              <div data-testid="priority-dismissed-section" className="mt-4">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-[11px] font-bold tracking-wider text-gray-300">{t("pi.dismissed_divider", "DISMISSED")} ({data.dismissed_total})</span>
                  <div className="flex-1 h-px bg-gray-100" />
                </div>
                {buckets.dismissed.map((s) => <PriorityCard key={s.thread_id} sig={s} onOpen={openThread} onFeedback={feedback} dismissed />)}
              </div>
            )}
          </div>
        </>
      )}
    </PageShell>
  );
}
